// Fiók-lista (full-page) — a KÖZÖS DataTable-re épül (a clients.index / contacts / employees a referencia-minta):
// szerver-oldali kereső (300 ms debounce) + oszlop-szűrők (típus/kategória/státusz) + darabszám-meta a toolbarban,
// többoszlopos szerver-rendezés (a legacy fiók-listát követve: sok technikai oszlop), végtelen görgetés (100/lap).
// A /accounts BFF-végpontról fogyaszt (Odin `clients/account` domain-resource + foundation ügyfél-név-merge).
//
// Legacy-parity (régi CRM „Fiókok" lista) — oszlop-sorrend: Fiók (név + weboldal + típus[semleges]/státusz/
// kategória-badge + „ellenőrzött" jel) · Cím · Ügyféladminisztrátor (account_affiliations kind='admin', identity-
// névre feloldva) · Kapcsolattartó (kind='contact') · Szolgáltatások (aktív service-plan-assignment-ek →
// szolgáltatás-nevek, best-effort) · Egyéb domain(ek) · Jegyzet. Az admin/kapcsolattartó/szolgáltatás dúsítás a
// BFF-ben batch-elt (affiliations + billing katalógus-lánc). A fiók-írás/-törlés/-export MÖGÖTT nincs Atlas-
// backend (csak `accounts.view` jog + read-only proxy). A sor-akció EGYETLEN „megtekintés" gomb (szem) + a
// sor-kattintás → a READ-ONLY fiók-detail (`/accounts/$id`); írás/törlés MÉG nincs (`accounts.manage` jog + a
// szerkesztő-form a write-fázisban jön), ezért nincs ceruza/kuka.
import { type AccountRowSchema } from '@atlas/contracts';
import { ArrowDownTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AccountCategoryBadge } from '@/components/accounts/account-category-badge';
import { DomainBadge } from '@/components/shared/domain-badge';
import { AccountStatusBadge } from '@/components/accounts/account-status-badge';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListExportButton } from '@/components/shared/list-export-button';
import { ListNewButton } from '@/components/shared/list-new-button';
import {
  DeleteRowAction,
  EditRowAction,
  RowActions,
  ViewRowAction,
} from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonRefCell } from '@/components/shared/person-ref-cell';
import { Text } from '@/components/ui/text';
import { accountKeys } from '@/features/accounts/api';
import { useListExport } from '@/hooks/use-list-export';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { toHref } from '@/lib/external-url';
import { isForbiddenStatus } from '@/lib/error-status';

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a route-nak
// ismernie kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül az URL-be.
const accountsListSearchSchema = z.object({
  q: z.string().optional(),
  /**
   * DEEP-LINK ügyfél-szűkítés (az ügyfél-adatlap „Fiókok" szekciójának „Részletes megtekintés" belépője).
   *
   * Nem felületi szűrő: a felület nem kínál ügyfél-legördülőt (a konkrét ügyfélre a kereső szűr, és egy
   * több százas legördülő a popover felét vinné el) — ugyanaz a megoldás, mint a hozzárendelés-listán. A
   * „Szűrők törlése" sem nyúl hozzá: a linkelt nézet ügyfél-szűkítve marad, ahogy a hívó szánta.
   */
  clientId: z.string().optional(),
  type: z.string().optional(),
  category: z.string().optional(),
  service: z.string().optional(),
  status: z.string().optional(),
  sort: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/accounts/')({
  validateSearch: (search) => accountsListSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: AccountsPage,
});

type AccountRow = z.infer<typeof AccountRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.accounts.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';

// A legacy enum-kulcsok (adat-parity) — a címkék az i18n-ből, fallbackként míg az Odin reference-data betölt.
const TYPE_OPTIONS = ['hospitality', 'accommodation', 'spa', 'other_tourism', 'other_industry'];
const CATEGORY_OPTIONS = ['A++', 'A+', 'A', 'B', 'C'];
const STATUS_OPTIONS = ['active', 'pending', 'inactive'];

export function AccountsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('accounts.view');
  // Jog-vezérelt sor-akciók (a clients-mintára): accounts.manage → ceruza (szerkesztő), különben szem
  // (read-only detail); accounts.delete → kuka (ConfirmDialog + soft-delete). A tényleges izolációt/írás-jogot
  // az Odin RLS/scope adja — ez CSAK a UI-felajánlás (a BFF a .manage/.delete finom kaput külön kikényszeríti).
  const canManage = can('accounts.manage');
  const canDelete = can('accounts.delete');

  const [deleteTarget, setDeleteTarget] = useState<AccountRow | null>(null);
  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    type: ALL,
    category: ALL,
    service: ALL,
    status: ALL,
    sort: 'name',
    order: 'asc',
  });
  const q = urlFilters.q;
  // A DEEP-LINK ügyfél-szűkítés a route search-paramjából (nem a `useListFilters` kulcs-készletéből: nem
  // felületi szűrő, tehát a szűrő-törlés sem veheti le).
  const { clientId: deepLinkClientId } = Route.useSearch();
  const type = urlFilters.type;
  const category = urlFilters.category;
  const service = urlFilters.service;
  const status = urlFilters.status;
  const sort = urlFilters.sort;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // Többoszlopos szerver-rendezés (a fiók-lista sok technikai oszloppal): a `sort` a kulcs, az `order` az irány.

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: accounts.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  const filters = {
    q,
    type,
    category,
    service,
    status,
    sort,
    order,
    clientId: deepLinkClientId ?? '',
  };
  const listKey = accountKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.accounts.query({
        query: {
          q: q || undefined,
          clientId: deepLinkClientId,
          type: type === ALL ? undefined : type,
          category: category === ALL ? undefined : category,
          service: service === ALL ? undefined : service,
          status: status === ALL ? undefined : status,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  // Slug → hivatalos név feloldás az Odin reference-data-ból (egyetlen forrásigazság). Best-effort: jog nélkül
  // 403 (a tábla úgyis a hiba-állapotot mutatja, a refs adata nem renderel) — a slug a saját fallbackje.
  const refsQ = api.accountRefs.useQuery(['account-refs'], {});
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;
  // A TÍPUS a jelvényen: refs → i18n → nyers slug. Korábban a refs betöltéséig (vagy 403-nál) a NYERS
  // slugot írta a jelvényre — a típus-szűrő viszont már az i18n-címkére esett vissza; most a kettő egyezik.
  const typeLabel = (v: string): string =>
    refs?.type[v] ?? t(`accounts.typeLabel.${v}`, { defaultValue: v });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.accounts : [])),
    [pages],
  );
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A szolgáltatás-facet opciói a BFF-től (distinct id+név a teljes listából) — a lapok között stabil (első ok-lap).
  const serviceOptions = okPage?.status === 200 ? okPage.body.serviceOptions : [];
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters =
    q !== '' || type !== ALL || category !== ALL || service !== ALL || status !== ALL;

  // Rendezés: azonos oszlopra kattintás → irányváltás; új oszlopra → az adott kulcs ASC.
  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort ? { order: order === 'asc' ? 'desc' : 'asc' } : { sort: col, order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista lista-sor eltávolítás + rollback): a soft-delete a BFF `/accounts/:id` (accounts.delete).
  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.accountDelete.mutation({ params: { id } }),
    invalidateKeys: [accountKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        if (!old) return old;
        const id = deleteTarget?.id;
        if (!id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    accounts: p.body.accounts.filter((a) => a.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:accounts.deleted'),
      conflict: t('toast:accounts.conflict'),
      forbidden: t('toast:accounts.forbidden'),
      validation: t('toast:accounts.validation'),
      error: t('toast:accounts.error'),
    },
  });

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
  }

  // A típus-szűrő opciói a reference-data-ból (fallback: az i18n-címkék, míg a refs betölt).
  const typeOptions =
    refs && Object.keys(refs.type).length > 0
      ? Object.entries(refs.type).map(([value, l]) => ({ value, label: l }))
      : TYPE_OPTIONS.map((o) => ({
          value: o,
          label: t(`accounts.typeLabel.${o}`, { defaultValue: o }),
        }));

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;
  // EXPORT: a sorok a lista-végpontról, az AKTÍV szűrőkkel (q + 4 facet + rendezés), 200-as lapokkal a végéig.
  const fetchExportPage = useCallback(
    async (offset: number | undefined) => {
      const r = await api.accounts.query({
        query: {
          q: q || undefined,
          type: type === ALL ? undefined : type,
          category: category === ALL ? undefined : category,
          service: service === ALL ? undefined : service,
          status: status === ALL ? undefined : status,
          sort,
          order,
          limit: 200,
          offset: offset ?? 0,
        },
      });
      if (r.status !== 200) return null;
      return { rows: r.body.accounts, next: r.body.nextOffset };
    },
    [q, type, category, service, status, sort, order],
  );

  // Személy-refek EXPORT-alakja: locale-tudatos nevek sortöréssel (a legördülő mögöttiek is).
  const personRefExport = (items: AccountRow['admins']): string =>
    items
      .map((x) =>
        formatName(
          { givenName: x.givenName, familyName: x.familyName, displayName: x.name },
          i18n.language,
        ),
      )
      .join('\n');
  // SZEMÉLY-kötődések (ügyféladminisztrátor/kapcsolattartó) cellája — a KÖZÖS `PersonRefCell` (ugyanezt a
  // cellát a kapcsolattartó-lista is rendereli; a feliratok modul-specifikus kulcsokból jönnek).
  type PersonRef = AccountRow['admins'][number];
  const personRefCell = (items: PersonRef[]): JSX.Element => (
    <PersonRefCell
      items={items}
      moreLabel={(n) => t('accounts.more', { count: n })}
      triggerAriaLabel={t('accounts.moreItems')}
    />
  );
  // Szolgáltatás-cella (nem személy): a szöveg a DataTable cella-stílusát örökli (13/muted) — a személy-
  // nevekkel EGYEZŐ méret/szín → konzisztens sor. Üres → em-dash, több → ExpandableCell.
  const serviceRefCell = (items: { id: string; name: string }[]): JSX.Element =>
    items.length === 0 ? (
      emDash
    ) : (
      <ExpandableCell
        items={items}
        getKey={(x) => x.id}
        visible={2}
        threshold={3}
        moreLabel={(n) => t('accounts.more', { count: n })}
        triggerAriaLabel={t('accounts.moreItems')}
        renderItem={(x) => <span className="block truncate">{x.name}</span>}
      />
    );

  const columns: DataTableColumn<AccountRow>[] = [
    {
      // 1. Fiók — név (rendezhető) + weboldal + típus/státusz/kategória-badge (a legacy 1. oszlopa).
      id: 'name',
      header: t('accounts.columns.account'),
      sortable: true,
      cardLabel: t('accounts.columns.account'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      // A cella három adatot mutat (név · weboldal · kategória/típus badge-ek) → az exportban ugyanez, de
      // sortöréssel és a badge-ek FELOLDOTT címkéjével (a nyers kulcs — pl. `hotel_4star` — nem olvasható).
      exportValue: (a) =>
        [
          a.name,
          a.website,
          [a.category, a.type ? typeLabel(a.type) : ''].filter(Boolean).join(' · '),
        ]
          .filter((x) => x && String(x).trim() !== '')
          .join('\n'),
      cell: (a) => (
        <div className="min-w-0 space-y-1.5">
          {/* Név + URL egy SZOROS „azonosító"-egység (~2px) — best practice: a cím és a közvetlen al-sora egy
              vizuális blokk; a nagyobb köz (space-y-1.5) csak a badge-csoportot választja el a név-blokktól. */}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <Text variant="itemTitle" as="span" className="truncate">
                {a.name}
              </Text>
            </div>
            {a.website && (
              <a
                href={toHref(a.website)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-brand-hover mt-0.5 block truncate text-xs hover:underline"
              >
                {a.website}
              </a>
            )}
          </div>
          {/* Kategória + típus a név alatt; a STÁTUSZ külön, dedikált UTOLSÓ oszlopban (egységes minden listán). */}
          {(!!a.type || !!a.category) && (
            <div className="flex flex-wrap items-center gap-1">
              {/* Sorrend: kategória (kompakt „minőség"-pill, mindig elöl) → típus (semleges). */}
              {a.category && <AccountCategoryBadge category={a.category} />}
              {a.type && (
                <DomainBadge kind="accountType" value={a.type} label={typeLabel(a.type)} />
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      // 2. Cím — irányítószám + város / utca / ország (a legacy „Cím" oszlop, rendezhető).
      id: 'address',
      card: 'subtitle',
      header: t('accounts.columns.address'),
      sortable: true,
      cardLabel: t('accounts.columns.address'),
      width: 'minmax(0,1.5fr)',
      className: 'text-muted-foreground',
      multiline: true,
      exportValue: (a) =>
        [[a.zip, a.city].filter(Boolean).join(' '), a.address, a.countryName]
          .filter((x) => x && x.trim() !== '')
          .join(', '),
      cell: (a) => {
        // Irányítószám + város + utca EGY sorban (", "-vel fűzve), az ország külön sorban `tone="tertiary"`-vel
        // (13, halványabb + light súly — a tertiary rendszer-szabálya), mint a cím-sor (secondary).
        const cityLine = [a.zip, a.city].filter(Boolean).join(' ');
        const line1 = [cityLine, a.address].filter(Boolean).join(', ');
        if (!line1 && !a.countryName) return emDash;
        return (
          <div className="min-w-0 space-y-0.5">
            {line1 && <div className="truncate">{line1}</div>}
            {a.countryName && (
              <Text tone="tertiary" as="div" className="truncate">
                {a.countryName}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      // 3. Ügyféladminisztrátor — a fiók admin-kötődései (account_affiliations kind='admin'), identity-névre
      //    feloldva (BFF-merge). Több kötődés → ExpandableCell.
      id: 'admins',
      header: t('accounts.columns.clientAdmin'),
      cardLabel: t('accounts.columns.clientAdmin'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (a) => personRefExport(a.admins),
      cell: (a) => personRefCell(a.admins),
    },
    {
      // 4. Kapcsolattartó — a fiók contact-kötődései (account_affiliations kind='contact'), identity-névre
      //    feloldva. Több kötődés → ExpandableCell.
      id: 'contacts',
      header: t('accounts.columns.contact'),
      cardLabel: t('accounts.columns.contact'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (a) => personRefExport(a.contacts),
      cell: (a) => personRefCell(a.contacts),
    },
    {
      // 5. Szolgáltatások — a fiók aktív service-plan-assignment-jei → szolgáltatás-nevek (best-effort;
      //    billing-jog/adat híján üres). Több szolgáltatás → ExpandableCell.
      id: 'services',
      header: t('accounts.columns.services'),
      cardLabel: t('accounts.columns.services'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (a) => a.services.map((x) => x.name).join('\n'),
      cell: (a) => serviceRefCell(a.services),
    },
    {
      // 6. Domainek — a fiók NEM-FŐ domainjei a `clients/account-domains` táblából (a fő domain a Weboldal); a
      //    legacy „Egyéb domain(ek)" szabad szövegének utódja.
      id: 'domains',
      header: t('accounts.columns.domains'),
      sortable: true,
      cardLabel: t('accounts.columns.domains'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (a) => a.domains.join('\n'),
      cell: (a) => {
        const domains = a.domains;
        return domains.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell
            items={domains}
            getKey={(d, i) => `${d}-${i}`}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('accounts.more', { count: n })}
            triggerAriaLabel={t('accounts.moreItems')}
            renderItem={(d) => (
              <a
                href={toHref(d)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-brand-hover block truncate text-sm hover:underline"
              >
                {d}
              </a>
            )}
          />
        );
      },
    },
    {
      // 7. Jegyzet — szabad szöveg, két sorra vágva (a legacy „Megjegyzés" oszlop, rendezhető; a fejléc-név
      //    az ügyfél-listával egységesen „Jegyzet").
      id: 'note',
      header: t('accounts.columns.note'),
      sortable: true,
      cardLabel: t('accounts.columns.note'),
      width: 'minmax(0,1.5fr)',
      className: 'text-muted-foreground',
      multiline: true,
      exportValue: (a) => a.note,
      cell: (a) => (a.note ? <span className="line-clamp-2">{a.note}</span> : emDash),
    },
    {
      // 8. Státusz — dedikált UTOLSÓ (adat) oszlop, a sor-akciók (⋯) előtt; pill-badge (egységes minden listán).
      id: 'status',
      header: t('accounts.columns.status'),
      cardLabel: t('accounts.columns.status'),
      width: 'minmax(0,1fr)',
      exportValue: (a) =>
        a.status ? t(`accounts.statusLabel.${a.status}`, { defaultValue: a.status }) : '',
      // Badge-cella: `multiline` → overflow-hidden (NEM truncate) → keskenyítéskor NINCS „…" a pill után.
      multiline: true,
      cell: (a) =>
        a.status ? (
          <AccountStatusBadge
            status={a.status}
            label={t(`accounts.statusLabel.${a.status}`, { defaultValue: a.status })}
          />
        ) : (
          emDash
        ),
    },
  ];

  const exportCtl = useListExport<AccountRow, number>({
    columns,
    slug: 'accounts',
    fetchPage: fetchExportPage,
    filters: {
      q,
      type: type === ALL ? undefined : type,
      category: category === ALL ? undefined : category,
      status: status === ALL ? undefined : status,
    },
  });

  return (
    <AppShell
      // Mobilon a lebegő speed-dial FAB-ban jelenik meg (Keresés · AI mellett) — csak ha van létrehozási jog.
      // Desktopon a toolbar-ban van a gomb (lásd lent, `hidden md:block`).
      secondaryActions={[
        // MOBIL: a sáv export-gombja rejtve van — a FAB-ban elérhető marad (`xlsx`, formátum-választás
        // nélkül: a FAB egy koppintásos felület).
        {
          key: 'export',
          icon: <ArrowDownTrayIcon />,
          label: t('common.export.label'),
          onClick: () => exportCtl.run('xlsx'),
        },
      ]}
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('accounts.new'),
              onClick: () => void navigate({ to: '/accounts/new' }),
            }
          : undefined
      }
      toolbar={
        // KÖZÖS ListToolbar: kereső + „Szűrők (N)" popover (kategória → típus → státusz) + darabszám-meta +
        // „Új fiók" gomb (≥md). A szűrők a gomb mögé csukódnak → a sáv `min-h`-s, sosem tördel/csordul.
        // Szűrő-sorrend a legacy-parity szerint: kategória → típus → státusz.
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          filters={[
            {
              key: 'category',
              label: t('accounts.filterCategory'),
              value: category,
              onChange: (v: string) => setFilters({ category: v }),
              options: CATEGORY_OPTIONS.map((o) => ({ value: o, label: o })),
              allValue: ALL,
            },
            {
              key: 'type',
              label: t('accounts.filterType'),
              value: type,
              onChange: (v: string) => setFilters({ type: v }),
              options: typeOptions,
              allValue: ALL,
            },
            {
              key: 'service',
              label: t('accounts.filterService'),
              value: service,
              onChange: (v: string) => setFilters({ service: v }),
              options: serviceOptions.map((o) => ({ value: o.id, label: o.name })),
              allValue: ALL,
              // SZOLGÁLTATÁS-nevek (adatból) → nem kisbetűsítjük.
              preserveCase: true,
            },
            {
              key: 'status',
              label: t('accounts.filterStatus'),
              value: status,
              onChange: (v: string) => setFilters({ status: v }),
              options: STATUS_OPTIONS.map((s) => ({
                value: s,
                label: t(`accounts.statusLabel.${s}`, { defaultValue: s }),
              })),
              allValue: ALL,
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('accounts.loading')
              : !isError && !infinite.isLoading
                ? t('accounts.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
            <>
              <div className="hidden md:block">
                <ListExportButton
                  onExport={exportCtl.run}
                  pending={exportCtl.pending}
                  hasRows={!isError && rows.length > 0}
                />
              </div>
              <div className="hidden md:block">
                <ListNewButton
                  label={t('accounts.new')}
                  onClick={() => void navigate({ to: '/accounts/new' })}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            </>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<AccountRow>
          columns={columns}
          rows={rows}
          getRowId={(a) => a.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('accounts.noMatches')}
                description={t('accounts.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('accounts.empty')} description={t('accounts.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          onRowClick={(a) => void navigate({ to: '/accounts/$id', params: { id: a.id } })}
          rowActionsLabel={t('accounts.columns.actions')}
          // A clients-mintára: 2 gomb (szerkesztés/megtekintés + törlés) → `7rem`; törlés-jog nélkül 1 gomb → `4rem`.
          actionsColWidth={canDelete ? '7rem' : '4rem'}
          rowActions={(a) => (
            <RowActions>
              {canManage ? (
                <EditRowAction
                  label={t('accounts.edit')}
                  onClick={() => void navigate({ to: '/accounts/$id', params: { id: a.id } })}
                />
              ) : (
                <ViewRowAction
                  label={t('accounts.view')}
                  onClick={() => void navigate({ to: '/accounts/$id', params: { id: a.id } })}
                />
              )}
              {canDelete && (
                <DeleteRowAction label={t('accounts.delete')} onClick={() => setDeleteTarget(a)} />
              )}
            </RowActions>
          )}
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('accounts.deleteTitle')}
        description={t('accounts.deleteDesc', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('accounts.delete')}
        cancelLabel={t('accounts.cancel')}
        pendingLabel={t('accounts.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
